import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 40px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  display: inline-block;
  width: fit-content;
  background: linear-gradient(45deg, #818cf8, #c084fc);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;

  body.dark & {
    background: linear-gradient(45deg, #4cc9f0, #a855f7);
    background-clip: text;
    -webkit-background-clip: text;
  }
`;

const Entries = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Entry = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 0 40px;
  padding: 28px 0;
  border-top: 1px solid var(--card-border);

  &:last-child {
    border-bottom: 1px solid var(--card-border);
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const DateRange = styled.span`
  font-size: 16px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  color: var(--text);
  letter-spacing: 0.04em;
`;

const Company = styled.span`
  font-size: 22px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  line-height: 1.3;
  color: var(--text);
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Role = styled.h3`
  margin: 0;
  font-size: 22px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  color: var(--text);
`;

const Bullets = styled.ul<{ open: boolean }>`
  margin: 0;
  padding: 0 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
  max-height: ${p => (p.open ? '600px' : '0')};
  opacity: ${p => (p.open ? 1 : 0)};
  transition: max-height 0.35s ease, opacity 0.25s ease;
`;

const Bullet = styled.li`
  font-size: 16px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 400;
  color: var(--text);
  opacity: 0.85;
  line-height: 1.6;
`;

const Toggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  color: var(--text);
  opacity: 0.45;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: left;
  transition: opacity 0.2s ease;
  width: fit-content;

  &:hover {
    opacity: 0.9;
  }
`;

interface ExperienceData {
  role: string;
  company: string;
  dates: string;
  bullets: string[];
}

const EXPERIENCE: ExperienceData[] = [
  {
    role: 'Software Engineer',
    company: 'SoFi',
    dates: 'May 2026 – Present',
    bullets: [],
  },
  {
    role: 'Software Engineer',
    company: 'Dragonfruit AI',
    dates: 'Aug 2024 – Feb 2026',
    bullets: [
      'Designed the frontend consumption layer for a real-time device monitoring system, implementing a ref-counted WebSocket subscription model enabling distributed UI components to selectively subscribe to camera health and status updates.',
      'Enhanced a production Video Management System, improving synchronized multi-camera playback across WebRTC, HLS, and cloud streams with timestamp-aligned bounding box overlay rendering.',
      'Led migration of GraphQL APIs (Graphene + Relay) from single-tenant to scoped multi-tenant architecture, refactoring authorization and resolver layers to enable cross-customer administration.',
      'Replaced linear crossing filters with cubic Bézier-based geometry for object-crossing detection in low-FPS video streams, deriving tangent and normal intersection logic.',
      'Built containerized Shopify applications using Node.js and Express with OAuth flows, webhook ingestion, and BullMQ-based background job systems on DynamoDB.',
    ],
  },
  {
    role: 'Graduate Teaching Assistant',
    company: 'University of Southern California',
    dates: 'Aug 2023 – Dec 2023',
    bullets: [
      'Led weekly 2-hour lab sections for 120+ students in Object-Oriented Programming (Java, C++).',
      'Designed and evaluated programming assignments focused on systems concepts including hash table implementation and performance analysis.',
    ],
  },
  {
    role: 'Software Engineering Intern',
    company: 'Dragonfruit AI',
    dates: 'Jan 2024 – May 2024',
    bullets: [
      'Designed a reusable data listing framework supporting list and queue modes, client/server sorting and filtering, and persisted user preferences for flexible security alert triage workflows.',
      'Implemented request-level logging and tracing using Atatus with UUID-based request tracking, improving observability and debugging of backend workflows.',
    ],
  },
  {
    role: 'Software Engineering Intern',
    company: 'Dragonfruit AI',
    dates: 'May 2023 – Aug 2023',
    bullets: [
      'Automated a video ingestion → annotation → correction pipeline using Celery workers with AWS S3, ElasticSearch, CVAT APIs, and FiftyOne to accelerate computer vision retraining.',
      'Built a React + Redux interface to configure and manage scheduled ingestion workflows with detection filters, time windows, and cron-based job orchestration.',
    ],
  },
];

const ExperienceEntry: React.FC<{ entry: ExperienceData }> = ({ entry }) => {
  const [open, setOpen] = useState(false);
  return (
    <Entry>
      <Meta>
        <Company>{entry.company}</Company>
      </Meta>
      <Body>
        <TitleRow>
          <Role>{entry.role}</Role>
          <DateRange>{entry.dates}</DateRange>
        </TitleRow>
        {entry.bullets.length > 0 && (
          <>
            <Toggle onClick={() => setOpen(o => !o)}>
              {open ? '− hide' : '+ details'}
            </Toggle>
            <Bullets open={open}>
              {entry.bullets.map((b, i) => <Bullet key={i}>{b}</Bullet>)}
            </Bullets>
          </>
        )}
      </Body>
    </Entry>
  );
};

const Experience: React.FC = () => (
  <Container>
    <Heading>Experience</Heading>
    <Entries>
      {EXPERIENCE.map((e, i) => <ExperienceEntry key={i} entry={e} />)}
    </Entries>
  </Container>
);

export default Experience;
